import { IframeLayout, SeparatorCurve, SeparatorSun, SeparatorMoon, Image, getLuminance } from 'vopay';
import { useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useHistoryFields } from '../providers/History.provider';
import { useLayout } from '../providers/Layout.provider';
import { usePaylink } from '../providers/Paylink.provider';
import { useToken } from '../providers/Token.provider';
import { useAccountVerification } from '../providers/AccountVerification.provider';
import useStorage from '../hooks/useStorage';
import ConnectionRoutes from '../components/Connect/Routes';
import useTranslationsOverride from '@/hooks/useTranslationsOverride';
import Welcome from '@/components/Messages/Welcome';
import { LanguageSwitcher } from 'UI';

type RedirectProps = {
  isRedirect: boolean;
};

const STEP_APP_MOUNTED = 'APP_MOUNTED';

const Layout = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const { i18n } = useTranslation('translations', { keyPrefix: 'BankConnection' });
  const { Token, isInitalized, paylink, paylinkSuccess } = usePaylink();
  const { isProcessing, isSetByBrowser, token } = useToken();
  const { isVerification, isLoaded } = useAccountVerification();
  const { color, layout, src, height, positionX, positionY, hasBackButton, hasChangeLanguage, pageTitle } = useLayout();
  const storage = useStorage();
  const { padInfo } = storage.padAgreement.getPadInfo();
  const { pageCount } = useHistoryFields();
  const { state } = useLocation<RedirectProps>();
  const [hasSetupRun, setHasSetupRun] = useState(false);
  const [hasHeader, setHasHeader] = useState(true);
  const [isOnlineConnection, setIsOnlineConnection] = useState(false);
  const done = useMemo(
    () => !isProcessing && (isInitalized || !paylinkSuccess) && (!isVerification || isLoaded),
    [isProcessing, isInitalized, paylinkSuccess, isVerification, isLoaded],
  );
  const { translationsInitialized } = useTranslationsOverride({ enabled: done });

  useEffect(() => {
    if (Token && !isSetByBrowser && !isProcessing && isInitalized && !state?.isRedirect && token) {
      if (paylink.Status === 'pending' && padInfo === '') {
        history.push('/welcome');
      } else if (paylink.Status === 'completed') {
        history.push('/success');
      } else if (paylink.Status === 'declined' || paylink.Status === 'cancelled') {
        history.push('/error');
      } else if (paylink.Status === '' && paylinkSuccess) {
        history.push('/welcome');
      }
    }

    if (isVerification) {
      history.push('/connect/account-verification');
    }
  }, [isSetByBrowser, isProcessing, isInitalized, isVerification, token]);

  useEffect(() => {
    if (pathname === '/connnect/fiserv/success') {
      setHasHeader(false);
    }

    // when using the Fiserv iframe, the back button doesn't work. when the pathname is '/connect/bank/:BankId/online', it should navigate back to '/connect/bank'
    const regex = /^\/connect\/bank\/(\d+)\/online$/;
    const match = regex.exec(pathname);

    setIsOnlineConnection(match !== null);
  }, [pathname]);

  useEffect(() => {
    if (!hasSetupRun) {
      // Run the setup code only if it hasn't run before

      const languagesSupported = ['en', 'fr', 'fr-CA'];
      const languageToUse = languagesSupported.includes(navigator.language) ? navigator.language : 'en';

      void i18n.changeLanguage(languageToUse);

      // Update the flag to indicate that setup has run
      setHasSetupRun(true);
    }
  }, [hasSetupRun]);

  const isWelcomePage = location.pathname.includes('welcome');

  useEffect(() => {
    const message = {
      Step: STEP_APP_MOUNTED,
    };

    window.top?.postMessage(message, '*');

    if (window.parent && window.parent !== window.top) {
      window.parent.postMessage(message, '*');
    }
  }, []);

  return (
    <div
      style={{
        opacity: translationsInitialized ? 1 : 0,
        transition: 'opacity 1s',
      }}
    >
      {done &&
        (isWelcomePage ? (
          <Welcome />
        ) : (
          <IframeLayout
            primaryColor={color}
            pageTitle={pageTitle}
            backButtonIconColor={token?.DarkMode ? 'primary-light' : 'primary-dark'}
            onBack={() => {
              if (isOnlineConnection) {
                history.push('/connect/bank');
              } else {
                history.goBack();
              }
            }}
            separator={
              layout === 'curve' ? (
                <SeparatorCurve width={120} height={110} fill={color} />
              ) : layout === 'sun' ? (
                <SeparatorSun width={100} height={110} fill={color} />
              ) : layout === 'moon' ? (
                <SeparatorMoon width={140} height={110} fill={color} />
              ) : (
                <></>
              )
            }
            image={
              src === '' ? (
                <></>
              ) : (
                <Image width="auto" height={height} positionX={positionX} positionY={positionY} src={src} />
              )
            }
            hasHeader={hasHeader}
            hasBackButton={hasBackButton}
            color={getLuminance(color) > 0.5 ? 'none' : 'none-light'}
            pageCount={pageCount}
            topRight={hasChangeLanguage ? <LanguageSwitcher /> : undefined}
            content={<ConnectionRoutes />}
          />
        ))}
    </div>
  );
};

export default Layout;
